import { Link, createFileRoute } from '@tanstack/react-router'
import type { ScratchProject } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { ErrorState, LoadingState } from '@/components/app/states'
import { formatDateTime } from '@/components/app/format'
import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'

export const Route = createFileRoute('/ops/scratch/')({
  loader: () => opsApi<{ projects: ScratchProject[] }>('/ops/scratch').then((r) => r.projects),
  head: () => ({ meta: [{ title: 'New sites · Ops' }] }),
  component: ScratchListPage,
  pendingComponent: () => <LoadingState label="Loading projects" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load projects" />,
})

function ScratchListPage() {
  const projects = Route.useLoaderData()
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <h1 className="text-base font-semibold">
            Sites from scratch <span className="font-normal text-muted-foreground">({projects.length})</span>
          </h1>
          <p className="text-muted-foreground">Briefs for new sites built on our infrastructure.</p>
        </div>
        <Link to="/ops/scratch/new" className={buttonVariants({ size: 'sm' })}>
          New site
        </Link>
      </div>
      {projects.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>No projects yet</EmptyTitle>
            <EmptyDescription>Start one with New site.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Files</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="py-1">
                    <Link to="/ops/scratch/$projectId" params={{ projectId: p.id }} className="font-medium underline-offset-4 hover:underline">
                      {p.businessName}
                    </Link>
                    <div className="text-xs text-muted-foreground">{p.domain}</div>
                  </TableCell>
                  <TableCell className="py-1">
                    <Badge variant="outline">{p.status}</Badge>
                  </TableCell>
                  <TableCell className="py-1 whitespace-nowrap text-muted-foreground">
                    {p.logoKey ? 'Logo, ' : ''}
                    {p.images.length} {p.images.length === 1 ? 'image' : 'images'}
                  </TableCell>
                  <TableCell className="py-1 whitespace-nowrap text-muted-foreground">{formatDateTime(p.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
